import { useRouter } from "@/_router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/controller";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  CopyIcon,
  DownloadIcon,
  SparklesIcon,
  MoreHorizontalIcon,
  FileTextIcon,
} from "@/components/Icons";
import Container from "@/components/Container";
import MarkdownContent from "@/components/MarkdownContent";
import { exportBaseName } from "@/transcript-export";
import { useUISlice } from "@/models/useUISlice";
import { promptAiInstructions } from "@/models/useAiInstructions";
import { downloadFile } from "@/file-download";
import removeMd from "remove-markdown";
import toast from "react-hot-toast";
import { createActionError } from "@/client-error";
import { actions, runtime } from "@/controller";
import MenuItem from "@/components/MenuItem";
import BackgroundAmbience from "@/components/BackgroundAmbience";
import PageHeader from "@/components/PageHeader";
import DropdownMenu from "@/components/DropdownMenu";
import CircularProgress from "@/components/CircularProgress";
import BottomActionBar from "@/components/BottomActionBar";
import { logClientEvent } from "@/telemetry";
import { recordFeatureUsed } from "@/models/feature-adoption";
import { useStillWorking } from "@/models/useStillWorking";

// ─── Loading State ──────────────────────────────────────────

const LoadingState = ({
  progress,
  customizing,
}: {
  progress: number;
  customizing?: boolean;
}) => {
  const { t } = useTranslation();
  const stillWorking = useStillWorking(true);

  return (
    <div className="flex flex-col items-center justify-center text-center pt-24 pb-16">
      <CircularProgress
        progress={progress}
        size="md"
        indeterminate={stillWorking}
      />
      <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2 tracking-tight">
        {stillWorking
          ? t("aiContent.stillWorkingTitle")
          : customizing
            ? t("summaryPage.customizingTitle")
            : t("summary.generating")}
      </h3>
      <p className="text-[14px] text-[var(--text-tertiary)] leading-relaxed max-w-[260px]">
        {stillWorking
          ? t("aiContent.stillWorkingSubtitle")
          : customizing
            ? t("summaryPage.customizingSubtitle")
            : t("summary.generatingSubtitle")}
      </p>
    </div>
  );
};

// ─── Empty / Failed State ───────────────────────────────────

const EmptyState = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center justify-center text-center pt-24 pb-16 px-6">
      <div className="w-20 h-20 rounded-full bg-[var(--fill-2)] flex items-center justify-center mb-6">
        <FileTextIcon size={32} className="text-[var(--text-muted)]" />
      </div>
      <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2 tracking-tight">
        {t("summary.failedTitle")}
      </h3>
      <p className="text-[14px] text-[var(--text-tertiary)] leading-relaxed max-w-[280px] mb-8">
        {t("summary.failedSubtitle")}
      </p>
      <button
        onClick={onRetry}
        data-action-id="summary.regenerate"
        className="btn btn-primary w-full max-w-[300px] text-[15px] justify-center active:scale-[0.97] transition-all"
      >
        {t("summary.tryAgain")}
      </button>
    </div>
  );
};

// ─── Main Page ──────────────────────────────────────────────

export default function SummaryPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const transcriptId = router.query?.id;
  const transcripts = useAppStore((state) => state.transcripts);
  const transcript = useMemo(
    () => transcripts.find((tr) => tr.id === transcriptId),
    [transcripts, transcriptId]
  );

  const [isScrolled, setIsScrolled] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customizing, setCustomizing] = useState(false);


  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout>(null);
  const generationHandleRef = useRef<ReturnType<
    typeof actions.aiContent.generate
  > | null>(null);
  const generationIdRef = useRef(0);

  useEffect(() => {
    if (transcriptId && !transcript) {
      void actions.transcript.get(transcriptId);
    }
  }, [transcriptId, transcript]);

  const generateSummary = useCallback(async (instructions?: string) => {
    if (!transcriptId) return;
    generationIdRef.current += 1;
    const generationId = generationIdRef.current;
    generationHandleRef.current?.cancel();
    setCustomizing(Boolean(instructions));
    setLoading(true);
    setProgress(0);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setProgress((prev) => Math.min(99, prev + 3));
    }, 1000);
    const handle = actions.aiContent.generate({
      feature: "summary",
      transcriptId,
      instructions,
    });
    generationHandleRef.current = handle;
    const completed = await handle.done;
    if (generationId !== generationIdRef.current) return;
    generationHandleRef.current = null;
    // Completion is driven by the poll handle, not by watching the store:
    // during a regeneration the OLD summary is still cached for a moment,
    // and a store watcher would declare completion instantly, flash the
    // failed state once the cleared transcript lands, and never show the
    // regenerating spinner.
    if (intervalRef.current) clearInterval(intervalRef.current);
    setLoading(false);
    setCustomizing(false);
    if (!("error" in completed)) {
      setProgress(100);
      recordFeatureUsed("summary");
    }
  }, [transcriptId]);

  useEffect(() => {
    if (!transcriptId || !transcript) return;
    if (transcript.summary) return;
    // A custom regeneration clears the stored summary server-side; the
    // in-flight guard keeps this auto-start from cancelling that run.
    if (generationHandleRef.current?.key === `${transcriptId}:summary`) return;
    void generateSummary();
  }, [transcriptId, transcript?.id, Boolean(transcript?.summary), generateSummary]);

  // Unmount-only: abandon whatever generation/poll is still in flight.
  useEffect(
    () => () => {
      generationIdRef.current += 1;
      generationHandleRef.current?.cancel();
      generationHandleRef.current = null;
      if (intervalRef.current) clearInterval(intervalRef.current);
    },
    [],
  );

  // Regenerate always collects instructions — there is no plain regenerate.
  const handleCustomize = () => {
    if (!transcriptId) return;
    if (!useAppStore.getState().isSubscribed) {
      useUISlice.getState().setPaywallVisible({
        utmSource: "web:summary --> customize",
      });
      return;
    }
    promptAiInstructions("summary", (instructions) => {
      void generateSummary(instructions);
    });
  };

  const scrollRafRef = useRef<number | null>(null);
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    if (scrollRafRef.current !== null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      setIsScrolled(scrollTop > 10);
      scrollRafRef.current = null;
    });
  }, []);

  const handleCopy = async () => {
    if (!transcript?.summary) return;
    logClientEvent("summary.copied", { transcriptId });
    await runtime.clipboard.write(removeMd(transcript.summary));
    toast.success(t("actions.copySuccess"));
  };

  const handleDownloadPdf = async () => {
    if (!transcript?.summary) return;
    const toastId = toast.loading(t("utils.downloadingFile"));
    try {
      const { marked } = await import("marked");
      // breaks: the on-screen summary keeps single newlines
      // (whitespace-pre-wrap), so the PDF must too instead of merging lines.
      const htmlContent = await marked(transcript.summary, { breaks: true });
      // dir="auto" matches the on-screen MarkdownContent: an Arabic summary
      // lays out right-to-left instead of inheriting the PDF page's LTR.
      const result = await actions.aiContent.markdownToPdf(
        transcript.id!,
        `<div dir="auto">${htmlContent}</div>`
      );
      if ("error" in result) throw createActionError(result.error);
      await downloadFile({
        type: "url",
        url: `data:application/pdf;base64,${result.value}`,
        fileName: `${exportBaseName(transcript.fileName)}.pdf`,
      });
    } catch (e) {
      console.error(e);
      toast.error(t("summary.failedTitle"));
    } finally {
      toast.dismiss(toastId);
    }
  };

  const handleDownloadText = () => {
    if (!transcript?.summary) return;
    downloadFile({
      type: "txt",
      text: removeMd(transcript.summary),
      fileName: `${exportBaseName(transcript.fileName)}.txt`,
    });
  };

  const handleDownloadMarkdown = () => {
    if (!transcript?.summary) return;
    downloadFile({
      type: "txt",
      text: transcript.summary,
      fileName: `${exportBaseName(transcript.fileName)}.md`,
    });
  };

  const title =
    transcript?.fileName?.replace(/\.[^/.]+$/, "") || t("home.untitled");

  return (
    <Container reading className="fixed inset-0 bg-[var(--bg-primary)] text-[var(--text-primary)] flex flex-col font-sans overflow-hidden selection:bg-[var(--accent)]/20">
      <BackgroundAmbience />

      <PageHeader isScrolled={isScrolled} onBack={() => router.back()}>
        {transcript?.summary && !loading && (
          <>
            {/* Regenerate = customize: always collects instructions first. */}
            <button
              onClick={handleCustomize}
              data-action-id="summary.regenerate"
              className="icon-btn"
            >
              <SparklesIcon size={20} />
            </button>
            <button
              onClick={() => setShowMore(!showMore)}
              data-action-id="summary.menu.toggle"
              className="icon-btn"
            >
              <MoreHorizontalIcon size={20} />
            </button>
          </>
        )}
      </PageHeader>

      <DropdownMenu open={showMore} onClose={() => setShowMore(false)}>
        <MenuItem
          actionId="summary.copy"
          icon={<CopyIcon size={16} />}
          label={t("summary.copy")}
          onClick={() => {
            setShowMore(false);
            handleCopy();
          }}
        />
        <MenuItem
          actionId="summary.downloadPdf"
          icon={<DownloadIcon size={16} />}
          label="PDF"
          onClick={() => {
            setShowMore(false);
            handleDownloadPdf();
          }}
        />
        <MenuItem
          actionId="summary.downloadText"
          icon={<FileTextIcon size={16} />}
          label="TXT"
          onClick={() => {
            setShowMore(false);
            handleDownloadText();
          }}
        />
        <MenuItem
          actionId="summary.downloadMarkdown"
          icon={<FileTextIcon size={16} />}
          label="Markdown"
          onClick={() => {
            setShowMore(false);
            handleDownloadMarkdown();
          }}
        />
      </DropdownMenu>

      <main
        className="flex-1 relative z-10 overflow-y-auto"
        onScroll={handleScroll}
      >
        <div className="px-5 md:px-8 max-w-3xl mx-auto">
          <div className="pt-4 pb-6">
            <div className="flex items-center gap-2 mb-2">
              <FileTextIcon size={14} className="text-[var(--text-muted)]" />
              <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest">
                {t("summary.title")}
              </span>
            </div>
            <h1 className="text-[26px] font-bold text-[var(--text-primary)] leading-tight tracking-tight">
              {title}
            </h1>
          </div>

          {loading && (
            <LoadingState progress={progress} customizing={customizing} />
          )}
          {!loading && transcript && !transcript.summary && (
            <EmptyState onRetry={() => void generateSummary()} />
          )}
          {!loading && transcript?.summary && (
            <div className="pb-4">
              <MarkdownContent content={transcript.summary} />

              {/* Liquid-glass hint pointing at the header control — the
                  discoverable path to customization without a competing
                  primary button under the content. Tappable all the same. */}
              <button
                data-action-id="summary.customize"
                onClick={handleCustomize}
                className="glass mt-8 w-full min-h-[var(--control-lg)] rounded-[28px] px-6 py-4 flex items-center justify-center gap-2.5 active:scale-[0.98] transition-all"
              >
                <SparklesIcon
                  size={16}
                  className="shrink-0 text-[var(--accent)]"
                />
                <span className="text-[13px] font-medium text-[var(--text-secondary)] leading-snug text-start">
                  {t("summaryPage.customizeHint")}
                </span>
              </button>
            </div>
          )}

          <div className="h-44" />
        </div>
      </main>

      {!loading && transcript?.summary && (
        <BottomActionBar compactBottom className="mx-auto flex w-fit max-w-full items-center justify-center gap-2">
          <button
            onClick={handleCopy}
            data-action-id="summary.copy"
            className="btn btn-glass h-16 w-16 shrink-0 flex-col gap-0.5 rounded-2xl p-0 text-[12px] active:scale-[0.97] transition-all"
          >
            <CopyIcon size={18} />
            <span>Copy</span>
          </button>
          <button
            onClick={handleDownloadPdf}
            data-action-id="summary.downloadPdf"
            className="btn btn-glass h-16 w-16 shrink-0 flex-col gap-0.5 rounded-2xl p-0 text-[12px] active:scale-[0.97] transition-all"
          >
            <DownloadIcon size={18} />
            <span>PDF</span>
          </button>
          <button
            onClick={handleDownloadText}
            data-action-id="summary.downloadText"
            className="btn btn-glass h-16 w-16 shrink-0 flex-col gap-0.5 rounded-2xl p-0 text-[12px] active:scale-[0.97] transition-all"
          >
            <FileTextIcon size={18} />
            <span>TXT</span>
          </button>
          <button
            onClick={handleDownloadMarkdown}
            data-action-id="summary.downloadMarkdown"
            className="btn btn-glass h-16 w-16 shrink-0 flex-col gap-0.5 rounded-2xl p-0 text-[12px] active:scale-[0.97] transition-all"
          >
            <FileTextIcon size={18} />
            <span>MD</span>
          </button>
        </BottomActionBar>
      )}
    </Container>
  );
}
